/**
 * Profile/Settings page: account, subscription, Alati (Pendulum), history, affiliate, gift and
 * payment sections, language, legal links. Signed-out visitors see the sign-in prompt plus Alati
 * (locked), the language select and the legal links.
 */
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Crown, Calendar, Settings, LogOut, Loader2, Users, Gift, Receipt, Copy, Check, Compass, Lock, History, ChevronRight, Wrench } from 'lucide-react';
import { Alert, AlertDescription, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { LegalLinks } from '@/components/features';
import { BookToolTile } from '@/components/features/book/BookCard';
import { useLanguage, useIsAuthenticated, useUser, useAuthStore, AVAILABLE_LANGUAGES, useSetLanguage } from '@/store';
import { useSubscriptionStatus, useCreatePortalSession } from '@/hooks';
import { translatePlanName, getProfileTranslation, getHeaderTranslation, formatDate, formatPrice, hasAccess, isTrialActive } from '@/utils';
import { ROUTES } from '@/config/routes';
import type { Language } from '@/types';

type ProfileT = (key: Parameters<typeof getProfileTranslation>[0]) => string;

/** Profil → Alati: Pendulum (Chakra stays hidden) and, when the server shows it, the book. Free and signed-out users see a lock on Pendulum. */
function ToolsCard({ locked, t }: { locked: boolean; t: ProfileT }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="mb-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" aria-hidden="true" />
            <span id={headingId}>{t('tools')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Link
            to={ROUTES.pendulum}
            className="flex min-h-11 items-center gap-3 rounded-lg border p-3 hover:bg-accent transition-colors"
          >
            <span className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Compass className="h-5 w-5 text-primary" aria-hidden="true" />
              {locked && (
                <Lock className="absolute -bottom-0.5 -right-0.5 h-4 w-4 text-lukaaji-gold" aria-hidden="true" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-medium">{t('pendulum')}</span>
              <span className="block text-sm text-muted-foreground">{t('pendulumDesc')}</span>
              {locked && (
                <span className="mt-1 block text-xs font-medium text-lukaaji-gold">{t('availableWithSubscription')}</span>
              )}
            </span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          </Link>
          {/* "Knjiga": its own state, never the subscription lock (ebook-plan D14, CR-22) */}
          <BookToolTile />
        </CardContent>
      </Card>
    </section>
  );
}

/** Language select; on phones this is the only language switch (the header has none below 768 px). */
function SettingsCard({ language, t }: { language: Language; t: ProfileT }) {
  const setLanguage = useSetLanguage();
  const selectId = useId();
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" aria-hidden="true" />
          {t('settings')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <label htmlFor={selectId}>{t('language')}</label>
          <select
            id={selectId}
            value={language}
            onChange={(e) => setLanguage(e.target.value as Language)}
            className="min-h-11 bg-background border rounded-md px-3 py-2 text-sm"
          >
            {AVAILABLE_LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.flag} {lang.nativeName}
              </option>
            ))}
          </select>
        </div>
      </CardContent>
    </Card>
  );
}

function formatLongDate(dateString: string | null, language: Language): string {
  if (!dateString) return '-';
  return formatDate(dateString, language, { month: 'long', day: 'numeric', year: 'numeric' });
}

export function ProfilePage() {
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const logout = useAuthStore((state) => state.logout);
  const { data: subscriptionStatus, isLoading: statusLoading } = useSubscriptionStatus();
  const createPortal = useCreatePortalSession();
  const [copiedLink, setCopiedLink] = useState(false);

  const t = (key: Parameters<typeof getProfileTranslation>[0]) => getProfileTranslation(key, language);

  // Redirect message for unauthenticated users
  if (!isAuthenticated) {
    return (
      <div className="container py-8 max-w-2xl">
        <div className="py-8 text-center">
          <User className="h-16 w-16 mx-auto text-muted-foreground mb-4" aria-hidden="true" />
          <h1 className="text-2xl font-bold mb-4">
            {t('signInToView')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('signInDesc')}
          </p>
          <Button asChild>
            <Link to={`${ROUTES.login}?redirect=${ROUTES.profile}`}>
              {t('signIn')}
            </Link>
          </Button>
        </div>
        <ToolsCard locked t={t} />
        <SettingsCard language={language} t={t} />
        <LegalLinks />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleManageSubscription = () => {
    createPortal.mutate();
  };

  const handleLogout = () => {
    logout();
  };

  const handleCopyReferralLink = async () => {
    const referralLink = `${window.location.origin}?ref=${user.id}`;
    await navigator.clipboard.writeText(referralLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Determine subscription status display. The status query is fresher than the stored sign-in.
  const isPremium = subscriptionStatus?.is_premium ?? user.is_premium;
  const hasTrial = Boolean(user.trial_expires);
  const trialActive = isTrialActive(user);
  // D3 grace: the period has ended and Stripe has not confirmed the next step; access lasts until access_until.
  const inGrace = Boolean(isPremium && subscriptionStatus?.in_grace);
  // Set to cancel: access stays until the period ends (Q6), it does not renew.
  const isCancelled = subscriptionStatus?.status === 'cancelled';
  const endsWithoutRenewal = inGrace || isCancelled;
  // Only a renewing plan waits for a payment; a cancelled one simply ends.
  const paymentPending = inGrace && !isCancelled;
  const paidAccessDate = inGrace
    ? subscriptionStatus?.access_until ?? null
    : subscriptionStatus?.subscription_expires ?? null;
  // Every plan and the trial get the same access (R2, R5).
  const contentAccess = subscriptionStatus?.can_access_premium ?? hasAccess(user);

  // Determine which premium features to show (based on subscription tier)
  // For now, show these sections to all premium users
  const showAffiliateSection = isPremium;
  const showGiftSection = isPremium;
  const showPaymentHistory = isPremium;

  const referralLink = `${window.location.origin}?ref=${user.id}`;

  return (
    <div className="container py-8 max-w-2xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">
          {t('profile')}
        </h1>
        <p className="text-muted-foreground">
          {t('manageAccountAndSubscription')}
        </p>
      </div>

      {/* Profile info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('accountInformation')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="font-semibold">{user.name}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {user.email}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            {t('subscription')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Status badge */}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  {t('status')}
                </span>
                {isPremium ? (
                  <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium px-3 py-1 rounded-full">
                    Premium
                  </span>
                ) : trialActive ? (
                  <span className="bg-blue-500 text-white text-sm font-medium px-3 py-1 rounded-full">
                    {t('freeTrial')}
                  </span>
                ) : (
                  <span className="bg-muted text-muted-foreground text-sm font-medium px-3 py-1 rounded-full">
                    {t('free')}
                  </span>
                )}
              </div>

              {/* Plan details */}
              {isPremium && subscriptionStatus?.plan && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t('plan')}
                  </span>
                  <span className="font-medium">
                    {translatePlanName(subscriptionStatus.plan, language)}
                  </span>
                </div>
              )}

              {/* Expiration */}
              {(isPremium || trialActive) && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {isPremium ? (endsWithoutRenewal ? t('accessUntil') : t('renews')) : t('trialEnds')}
                  </span>
                  <span className="font-medium">
                    {formatLongDate(isPremium ? paidAccessDate : user.trial_expires, language)}
                  </span>
                </div>
              )}

              {paymentPending && <p className="text-sm text-muted-foreground">{t('paymentPending')}</p>}

              {/* Trial expired alert */}
              {hasTrial && !trialActive && !isPremium && (
                <Alert>
                  <AlertDescription>
                    {t('trialExpiredAlert')}
                  </AlertDescription>
                </Alert>
              )}

              {/* Action buttons */}
              <div className="pt-4 space-y-3">
                {isPremium ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleManageSubscription}
                    disabled={createPortal.isPending}
                  >
                    {createPortal.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Settings className="h-4 w-4 mr-2" />
                    )}
                    {t('manageSubscription')}
                  </Button>
                ) : (
                  <Button asChild className="w-full">
                    <Link to={ROUTES.subscription}>
                      <Crown className="h-4 w-4 mr-2" />
                      {t('upgradeToPremium')}
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ToolsCard locked={!contentAccess} t={t} />

      {/* Listening history (reachable on phones only from here) */}
      <Card className="mb-6">
        <CardContent className="p-2">
          <Link
            to={ROUTES.history}
            className="flex min-h-11 items-center gap-3 rounded-md px-3 hover:bg-accent transition-colors"
          >
            <History className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <span className="flex-1 font-medium">{getHeaderTranslation('history', language)}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </Link>
        </CardContent>
      </Card>

      {/* Affiliate Program Section */}
      {showAffiliateSection && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('affiliateProgram')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Referral Link */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('yourReferralLink')}
              </label>
              <div className="flex gap-2">
                <Input
                  value={referralLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={handleCopyReferralLink}
                  className="flex-shrink-0"
                >
                  {copiedLink ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-primary">0</p>
                <p className="text-xs text-muted-foreground">
                  {t('totalReferrals')}
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-primary">0</p>
                <p className="text-xs text-muted-foreground">
                  {t('active')}
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-primary">{formatPrice(0)}</p>
                <p className="text-xs text-muted-foreground">
                  {t('earnings')}
                </p>
              </div>
            </div>

            {/* Recent referrals placeholder */}
            <div className="text-center py-4 text-muted-foreground text-sm">
              {t('shareToStartEarning')}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gift Subscriptions Section */}
      {showGiftSection && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5" />
              {t('giftSubscriptions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg p-4 text-center mb-4">
              <p className="text-sm mb-3">
                {t('shareGift')}
              </p>
              <Button variant="outline" size="sm">
                {t('purchaseGift')}
              </Button>
            </div>

            {/* Sent gifts placeholder */}
            <div className="text-center py-4 text-muted-foreground text-sm">
              {t('noGiftsSent')}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History Section */}
      {showPaymentHistory && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              {t('paymentHistory')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Payment history placeholder */}
            <div className="text-center py-4 text-muted-foreground text-sm">
              {t('paymentHistoryAppears')}
            </div>
          </CardContent>
        </Card>
      )}

      <SettingsCard language={language} t={t} />

      {/* Logout */}
      <Button
        variant="outline"
        className="w-full text-destructive hover:text-destructive"
        onClick={handleLogout}
      >
        <LogOut className="h-4 w-4 mr-2" />
        {t('signOut')}
      </Button>

      <LegalLinks className="mt-6" />
    </div>
  );
}
